import React, { useState } from "react";
import { ReuseQuestionSetModal } from "components/pages/QuestionSet/QuestionSetModals";
import { patchGroupAssignments } from "hooks/useCurriculum/useCurriculum";
import { logEducatorDashboard, LogLevel } from "utils";
import { compareCode } from "./assignmentDraft";

// The Question Carousel's "apply to siblings" offer (OKO2-3803).
//
// The My Question Set flow already asks, after a save, whether the other groups on
// the same topic should get the same thing. A filter saved from the carousel now
// asks the same question with the same popup; the difference is what "the same
// thing" is — there is no set to apply, so confirming PATCHes the just-saved
// question_filter onto the checked codes in one delta call.
//
// Applying the filter to a sibling that points at a saved set detaches that set
// (the delta PATCH drops set pointers for touched rows by design). That is the
// intended "use this same" semantics — the educator controls it per group through
// the checkbox list, which is why such siblings are offered rather than hidden.

// Other groups on the same topic (track + standard) in this bundle, the same rule
// the My Question Set flow uses for its offer list.
export const reuseFilterCandidates = (assignments, { groupCode, trackType, standardCode }) =>
  (assignments || [])
    .filter((a) => a.group_code !== groupCode
      && a.track_type === trackType
      && a.standard_code === standardCode
      && a.track_type !== "no_assignment")
    .map((a) => a.group_code)
    .sort(compareCode);

/**
 * @param {object|null} offer  Set after a successful carousel save when siblings
 *   exist: { groupCode, trackType, standardCode, standardName, filter, total,
 *   candidates }. `trackType` must already be spelled the way the endpoint stores
 *   the rows (the lesson flow maps to its singular form before building the offer).
 * @param {object} bundle  The bundle the mini card names itself from.
 * @param {string} classroomId
 * @param {string} assignmentId
 * @param {function} onClose  Dismiss without applying (also called after success).
 * @param {function} onApplied  Refresh callback after a successful apply.
 */
export const ReuseFilterModal = ({ offer, bundle, classroomId, assignmentId, onClose, onApplied }) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const confirm = async (codes) => {
    if (codes.length === 0) { onClose(); return; }
    setSaving(true);
    setError(null);
    try {
      await patchGroupAssignments(classroomId, assignmentId, codes.map((code) => ({
        group_code: code,
        track_type: offer.trackType,
        standard_code: offer.standardCode,
        question_filter: offer.filter,
      })));
      await onApplied?.();
      onClose();
    } catch (e) {
      logEducatorDashboard("ReuseFilterModal: failed to apply question filter to siblings", {
        level: LogLevel.WARNING,
        event: "question_filter_reuse_error",
        statusCode: e?.status,
        groupCode: offer?.groupCode,
        groupCount: codes.length,
      });
      setError(e?.detail || e?.message || "Failed to update the selected groups.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ReuseQuestionSetModal
      isOpen={!!offer}
      onClose={onClose}
      assignment={offer ? {
        group_code: offer.groupCode,
        track_type: offer.trackType,
        standard_code: offer.standardCode,
        standard_name: offer.standardName,
        question_filter: offer.filter,
      } : {}}
      bundle={bundle}
      questionCount={offer?.total}
      setLabel={offer?.filter ? "Filtered Questions" : "Full Question Bank"}
      groupCodes={offer?.candidates || []}
      saving={saving}
      error={error}
      onConfirm={confirm}
    />
  );
};
